import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CalendarScroller from './components/CalendarScroller.jsx';
import DragGhost from './components/DragGhost.jsx';
import Header from './components/Header.jsx';
import MonthBar from './components/MonthBar.jsx';
import SportComposer from './components/sport/SportComposer.jsx';
import SportSession from './components/sport/SportSession.jsx';
import { useDragDrop } from './hooks/useDragDrop.js';
import { useEndlessCalendar } from './hooks/useEndlessCalendar.js';
import { useHabits, useOccurrences } from './hooks/useHabits.js';
import { useToday } from './hooks/useToday.js';
import { toDateKey } from './utils/date.js';
import { completionKey, countProgress } from './utils/habits.js';
import { monthKeyOf, monthsDayRange } from './utils/month.js';
import { isPlaceableDraft, placementHint, placementPrompt } from './utils/placement.js';

export default function App() {
  const {
    habits,
    completions,
    sportLogs,
    storageFailed,
    addHabit,
    addSportHabit,
    deleteHabitOccurrence,
    moveHabitOccurrence,
    toggleDone,
    setSportField,
  } = useHabits();

  const [editMode, setEditMode] = useState(false);
  // Day whose inline composer is open, or null when none is.
  const [composerDate, setComposerDate] = useState(null);
  // The sport creation modal, opened from the header and so carrying no date.
  const [sportComposerOpen, setSportComposerOpen] = useState(false);
  // A finished sport draft waiting for a day to be tapped, or null.
  const [pendingSport, setPendingSport] = useState(null);
  // The sport occurrence whose full-page session window is open.
  const [sportSession, setSportSession] = useState(null);

  const placing = isPlaceableDraft(pendingSport);

  // Rolls over on its own at midnight — the app is often left open overnight.
  const today = useToday();
  const todayKey = toDateKey(today);
  const currentMonthKey = monthKeyOf(today);

  const { months, activeMonthKey, atToday, registerSection, scrollRef, goToToday } =
    useEndlessCalendar(currentMonthKey, todayKey);

  // Occurrences are computed for everything currently rendered, in one pass.
  const { startKey, endKey } = monthsDayRange(months);
  const { occurrences, byDate } = useOccurrences(habits, startKey, endKey);

  // The header score only counts the month you are looking at.
  const monthProgress = useMemo(
    () => countProgress(occurrences.filter((o) => o.date.startsWith(activeMonthKey)), completions),
    [occurrences, completions, activeMonthKey]
  );

  // Drag and drop: a dropped card moves that single occurrence to the new day.
  const handleDrop = useCallback(
    (occurrence, targetKey) => {
      moveHabitOccurrence(occurrence.habit, occurrence.baseDate, targetKey);
    },
    [moveHabitOccurrence]
  );
  const { drag, overKey, startDrag } = useDragDrop(handleDrop);
  const dragSourceId = drag ? `${drag.payload.habit.id}|${drag.payload.baseDate}` : null;

  // Editing, composing and placing are mutually exclusive modes.
  const toggleEdit = () => {
    setComposerDate(null);
    setPendingSport(null);
    setEditMode((value) => !value);
  };
  const openComposer = (dateKey) => {
    setEditMode(false);
    setComposerDate(dateKey);
  };

  /** Header "Sport": a second tap while placing abandons the pending draft. */
  const openSportComposer = () => {
    if (placing) {
      setPendingSport(null);
      return;
    }
    setEditMode(false);
    setComposerDate(null);
    setSportComposerOpen(true);
  };

  /** Composer finished: park the draft and let the calendar wait for a tap. */
  const createSport = (draft) => {
    setSportComposerOpen(false);
    setPendingSport(draft);
  };

  /** A day was tapped while placing — that day anchors the whole schedule. */
  const placeSport = (dateKey) => {
    if (!placing) return;
    addSportHabit(pendingSport, dateKey);
    setPendingSport(null);
  };

  // Escape backs out of placing, matching how both sport sheets close.
  useEffect(() => {
    if (!placing) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setPendingSport(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [placing]);

  // The session reads its values live from state, so typing updates the tick
  // and the card behind the overlay at the same time.
  const sessionValues = sportSession
    ? sportLogs[completionKey(sportSession.habit.id, sportSession.baseDate)] || {}
    : {};

  return (
    // Fixed-height shell: the header never moves, only <main> scrolls.
    <div className="mx-auto flex h-[100dvh] w-full max-w-7xl flex-col px-4 sm:px-6">
      <div className="shrink-0">
        <Header
          progress={monthProgress}
          editMode={editMode}
          placing={placing}
          onToggleEdit={toggleEdit}
          onAddSport={openSportComposer}
        />
        <MonthBar
          monthKey={activeMonthKey}
          currentMonthKey={currentMonthKey}
          atToday={atToday}
          onToday={() => goToToday()}
        />
        {editMode && (
          <p className="py-2 text-center font-hand text-lg text-accent">
            drag a habit onto another day, or ✕ to remove it
          </p>
        )}

        {/* The day-picking banner: what is being placed, what a tap will mean,
            and a way out that is not just the Escape key. */}
        {placing && (
          <div className="flex items-baseline justify-center gap-3 py-2 text-center">
            <p className="font-hand text-lg leading-tight text-accent">
              {placementPrompt(pendingSport)}
              <span className="block font-hand text-base text-ink-soft">
                {placementHint(pendingSport.repeat)}
              </span>
            </p>
            <button
              type="button"
              onClick={() => setPendingSport(null)}
              className="shrink-0 text-xs uppercase tracking-widest text-ink-soft
                         transition-colors hover:text-accent"
            >
              cancel
            </button>
          </div>
        )}

        {/* Private browsing or a full quota means nothing is being saved.
            Better to say so than to let a month of ticks evaporate. */}
        {storageFailed && (
          <p role="alert" className="rounded-lg bg-accent-soft px-3 py-1.5 text-center font-hand text-lg text-ink">
            can’t save to this browser — your notebook will be gone when you close the tab
          </p>
        )}
      </div>

      <main ref={scrollRef} className="no-scrollbar min-h-0 flex-1 overflow-y-auto">
        <CalendarScroller
          months={months}
          registerSection={registerSection}
          byDate={byDate}
          today={today}
          completions={completions}
          sportLogs={sportLogs}
          editMode={editMode}
          overKey={overKey}
          dragSourceId={dragSourceId}
          composerDate={composerDate}
          placing={placing}
          placingLabel={placing ? pendingSport.name : ''}
          onPickDay={placeSport}
          onToggle={toggleDone}
          onDelete={deleteHabitOccurrence}
          onAdd={openComposer}
          onOpenSport={setSportSession}
          onCreate={addHabit}
          onCloseComposer={() => setComposerDate(null)}
          onDragStart={startDrag}
        />
      </main>

      <DragGhost drag={drag} />

      {sportComposerOpen && (
        <SportComposer onCreate={createSport} onClose={() => setSportComposerOpen(false)} />
      )}

      {sportSession && (
        <SportSession
          occurrence={sportSession}
          values={sessionValues}
          sportLogs={sportLogs}
          onChange={(fieldKey, value) =>
            setSportField(sportSession.habit, sportSession.baseDate, fieldKey, value)
          }
          onClose={() => setSportSession(null)}
        />
      )}
    </div>
  );
}
